import { extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import tokens from '@te-digi/styleguide-tokens';
import { media } from '../utils/styles/media.js';

const getWidthStyle = width => {
  if (typeof width === 'object') {
    return Object.keys(width).map(breakpoint => breakpoint === 'xs' ? "width: ".concat(width.xs, "%;") : "".concat(media[breakpoint], " { width:\n        ").concat(width[breakpoint], "%; }")).join('\n');
  } else if (typeof width === 'number') {
    return "width: ".concat(width, "%;");
  } else {
    return;
  }
};
const getCSSProperty = (property, size, asNegative) => {
  if (!size && size !== 0) {
    return '';
  }
  const isNegativeSize = typeof size === 'string' && size.startsWith('n-') || asNegative;
  const absoluteSize = typeof size === 'number' || size === 'none' ? 0 : size.replace('n-', '');
  const space = tokens.space[absoluteSize] || absoluteSize;
  return "".concat(property, ": ").concat(isNegativeSize ? "-".concat(space) : space, ";");
};
const getSpacingStyle = function (property, size) {
  let asNegative = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (size === undefined || size === null) {
    return;
  }
  return typeof size === 'object' ? Object.keys(size).map(breakpoint => breakpoint === 'xs' ? getCSSProperty(property, size.xs, asNegative) : "".concat(media[breakpoint], " {").concat(getCSSProperty(property, size[breakpoint], asNegative), " }")).join('\n') : getCSSProperty(property, size, asNegative);
};
const StyledSpacer = styled.div.withConfig({
  componentId: "sg1510__sc-1nbk2wl-0"
})(["&&{", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", "}&:focus-visible{box-shadow:", ";border-radius:", ";outline:none;}"], props => getSpacingStyle('margin', props.$margin), props => getSpacingStyle('margin-bottom', props.$marginBottom), props => getSpacingStyle('margin-left', props.$marginLeft), props => getSpacingStyle('margin-right', props.$marginRight), props => getSpacingStyle('margin-top', props.$marginTop), props => getSpacingStyle('padding', props.$padding), props => getSpacingStyle('padding-bottom', props.$paddingBottom), props => getSpacingStyle('padding-left', props.$paddingLeft), props => getSpacingStyle('padding-right', props.$paddingRight), props => getSpacingStyle('padding-top', props.$paddingTop), props => getWidthStyle(props.$width), tokens.shadow.focus, tokens.radius.md);
const Spacer = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    as,
    children,
    className,
    id,
    margin,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    padding,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    role,
    width,
    ...rest
  } = _ref;
  return /*#__PURE__*/React__default.createElement(StyledSpacer, _extends({
    $margin: margin,
    $marginBottom: marginBottom,
    $marginLeft: marginLeft,
    $marginRight: marginRight,
    $marginTop: marginTop,
    $padding: padding,
    $paddingBottom: paddingBottom,
    $paddingLeft: paddingLeft,
    $paddingRight: paddingRight,
    $paddingTop: paddingTop,
    $width: width,
    as: as || 'div',
    className: className,
    id: id,
    ref: ref,
    role: role
  }, rest), children);
});
const PADDING_SIZES = [0, 'none', 'xxxs', 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl', 'layout-xxs', 'layout-xs', 'layout-sm', 'layout-md', 'layout-lg', 'layout-xl', 'layout-xxl'];
const MARGIN_SIZES = ['auto', ...PADDING_SIZES, 'n-xxxs', 'n-xxs', 'n-xs', 'n-sm', 'n-md', 'n-lg', 'n-xl', 'n-xxl', 'n-xxxl'];
const PADDING_SHAPE = {
  xs: PropTypes.oneOf(PADDING_SIZES),
  sm: PropTypes.oneOf(PADDING_SIZES),
  md: PropTypes.oneOf(PADDING_SIZES),
  lg: PropTypes.oneOf(PADDING_SIZES),
  xl: PropTypes.oneOf(PADDING_SIZES)
};
const MARGIN_SHAPE = {
  xs: PropTypes.oneOf(MARGIN_SIZES),
  sm: PropTypes.oneOf(MARGIN_SIZES),
  md: PropTypes.oneOf(MARGIN_SIZES),
  lg: PropTypes.oneOf(MARGIN_SIZES),
  xl: PropTypes.oneOf(MARGIN_SIZES)
};
const SPACER_WIDTH = [25, 50, 75, 100];
Spacer.displayName = 'Spacer';
Spacer.propTypes = {
  /**
   * Element or component used for the root node.
   */
  as: PropTypes.any,
  /**
   * Content of the component.
   */
  children: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  // @ts-expect-error
  margin: PropTypes.oneOfType([PropTypes.oneOf(MARGIN_SIZES), PropTypes.shape(MARGIN_SHAPE)]),
  // @ts-expect-error
  marginBottom: PropTypes.oneOfType([PropTypes.oneOf(MARGIN_SIZES), PropTypes.shape(MARGIN_SHAPE)]),
  // @ts-expect-error
  marginLeft: PropTypes.oneOfType([PropTypes.oneOf(MARGIN_SIZES), PropTypes.shape(MARGIN_SHAPE)]),
  // @ts-expect-error
  marginRight: PropTypes.oneOfType([PropTypes.oneOf(MARGIN_SIZES), PropTypes.shape(MARGIN_SHAPE)]),
  // @ts-expect-error
  marginTop: PropTypes.oneOfType([PropTypes.oneOf(MARGIN_SIZES), PropTypes.shape(MARGIN_SHAPE)]),
  // @ts-expect-error
  padding: PropTypes.oneOfType([PropTypes.oneOf(PADDING_SIZES), PropTypes.shape(PADDING_SHAPE)]),
  // @ts-expect-error
  paddingBottom: PropTypes.oneOfType([PropTypes.oneOf(PADDING_SIZES), PropTypes.shape(PADDING_SHAPE)]),
  // @ts-expect-error
  paddingLeft: PropTypes.oneOfType([PropTypes.oneOf(PADDING_SIZES), PropTypes.shape(PADDING_SHAPE)]),
  // @ts-expect-error
  paddingRight: PropTypes.oneOfType([PropTypes.oneOf(PADDING_SIZES), PropTypes.shape(PADDING_SHAPE)]),
  // @ts-expect-error
  paddingTop: PropTypes.oneOfType([PropTypes.oneOf(PADDING_SIZES), PropTypes.shape(PADDING_SHAPE)]),
  role: PropTypes.string,
  // @ts-expect-error
  width: PropTypes.oneOfType([PropTypes.oneOf(SPACER_WIDTH), PropTypes.shape({
    xs: PropTypes.oneOf(SPACER_WIDTH),
    sm: PropTypes.oneOf(SPACER_WIDTH),
    md: PropTypes.oneOf(SPACER_WIDTH),
    lg: PropTypes.oneOf(SPACER_WIDTH),
    xl: PropTypes.oneOf(SPACER_WIDTH)
  })])
};

export { Spacer, getSpacingStyle };
